/**
 * brain.ts — what the CMS teaches the AI about the brand, beyond approved rules:
 *   • a BRAND PROFILE (voice, audience, what to avoid), written by a manager;
 *   • a KNOWLEDGE LIBRARY (product facts, brand documents, past content, good ads), written by a manager,
 *     split into passages and retrieved per task, each passage cited as K1, K2…
 * Both are plain text stored in the CMS. Nothing is trained; the AI reads them on every call.
 */
import { z } from "zod";

export const KNOWLEDGE_KINDS = {
  product_fact: "Product or company fact",
  brand_doc: "Brand or style document",
  past_content: "Past content we are proud of",
  ad_example: "Ad that worked",
} as const;
export type KnowledgeKind = keyof typeof KNOWLEDGE_KINDS;
export const knowledgeKindSchema = z.enum(["product_fact", "brand_doc", "past_content", "ad_example"]);

/** Limits that keep a request cheap on the Free plan (a Worker may use about 10 ms of CPU per request). */
export const KNOWLEDGE_LIMITS = {
  maxDocsPerSite: 40,
  maxCharsPerDoc: 20_000,
  minCharsPerDoc: 40,
  maxChunksPerSite: 300,
  chunkChars: 900,
  maxPassagesInPrompt: 6,
  maxPassageChars: 1200,
} as const;

export const knowledgeRefSchema = z.string().regex(/^K\d{1,2}$/);

export const brandProfileSchema = z.object({
  /** How the brand sounds: tone, person, vocabulary. */
  voice: z.string().trim().max(3000).default(""),
  /** Who we write for. */
  audience: z.string().trim().max(1500).default(""),
  /** Things never to do or say. */
  avoid: z.string().trim().max(1500).default(""),
});
export type BrandProfile = z.infer<typeof brandProfileSchema>;
export const EMPTY_BRAND_PROFILE: BrandProfile = { voice: "", audience: "", avoid: "" };

export const brandProfileResponseSchema = brandProfileSchema.extend({
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
export type BrandProfileResponse = z.infer<typeof brandProfileResponseSchema>;
export const saveBrandProfileBodySchema = brandProfileSchema.extend({ siteId: z.string().min(1).max(100) });

export const knowledgeDocInputSchema = z.object({
  siteId: z.string().min(1).max(100),
  title: z.string().trim().min(2).max(200),
  kind: knowledgeKindSchema,
  body: z.string().trim().min(KNOWLEDGE_LIMITS.minCharsPerDoc).max(KNOWLEDGE_LIMITS.maxCharsPerDoc),
});
export type KnowledgeDocInput = z.infer<typeof knowledgeDocInputSchema>;

export const knowledgeDocSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  title: z.string(),
  kind: knowledgeKindSchema,
  charCount: z.number().int(),
  chunkCount: z.number().int(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
});
export type KnowledgeDoc = z.infer<typeof knowledgeDocSchema>;

export const knowledgeListResponseSchema = z.object({
  items: z.array(knowledgeDocSchema),
  usage: z.object({ docs: z.number().int(), chunks: z.number().int() }),
  limits: z.object({ maxDocsPerSite: z.number().int(), maxChunksPerSite: z.number().int(), maxCharsPerDoc: z.number().int() }),
});
export type KnowledgeListResponse = z.infer<typeof knowledgeListResponseSchema>;

/** One retrieved passage as the AI sees it. */
export const knowledgeItemSchema = z.object({
  ref: knowledgeRefSchema,
  id: z.string(),
  title: z.string().max(200),
  kind: knowledgeKindSchema,
  excerpt: z.string().max(KNOWLEDGE_LIMITS.maxPassageChars),
});
export type KnowledgeItem = z.infer<typeof knowledgeItemSchema>;

/** Splits a document into passages on paragraph breaks, never cutting a word, each about `target` characters. */
export function chunkKnowledge(body: string, target: number = KNOWLEDGE_LIMITS.chunkChars): string[] {
  const paragraphs = body.replace(/\r\n?/g, "\n").split(/\n{2,}/).map((p) => p.replace(/[ \t]+/g, " ").trim()).filter(Boolean);
  const out: string[] = [];
  let cur = "";
  const push = () => {
    if (cur.trim()) out.push(cur.trim());
    cur = "";
  };
  for (const para of paragraphs) {
    if (para.length > target) {
      push();
      let rest = para;
      while (rest.length > target) {
        const window = rest.slice(0, target);
        const cut = window.lastIndexOf(" ");
        const at = cut > target * 0.5 ? cut : target;
        out.push(rest.slice(0, at).trim());
        rest = rest.slice(at).trim();
      }
      cur = rest;
    } else if (cur && cur.length + para.length + 2 > target) {
      push();
      cur = para;
    } else {
      cur = cur ? `${cur}\n\n${para}` : para;
    }
  }
  push();
  return out;
}
